import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState, useImperativeHandle, memo, useLayoutEffect, forwardRef } from "react";
const getNumericTop = (val) => {
    if (typeof val === "number")
        return val;
    if (typeof val === "string") {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? null : parsed;
    }
    return null;
};
// Internal component that uses forwardRef for the DOM element
const ChartSplitterInner = memo(forwardRef(function ChartSplitterInner({ splitterIndex, available, onCommitDelta, onDoubleClick, className, style, title, minDeltaY, maxDeltaY, topHeight, bottomHeight, ariaLabel, badgeTopLabel, badgeBottomLabel, }, forwardedRef) {
    const [isDragging, setIsDragging] = useState(false);
    const [visualOffset, setVisualOffset] = useState(0);
    const [releasedTop, setReleasedTop] = useState(null);
    const rootRef = useRef(null);
    const dragRef = useRef(null);
    const commitRef = useRef(onCommitDelta);
    const availableRef = useRef(available);
    const pendingSyncRef = useRef(false);
    const handleRef = useRef(null);
    // Keep refs in sync with the latest props so callbacks are always current.
    useEffect(() => { commitRef.current = onCommitDelta; }, [onCommitDelta]);
    useEffect(() => { availableRef.current = available; }, [available]);
    // Create the imperative handle
    const handle = {
        syncGeometry: () => {
            // Called by parent after layout commit to ensure visual offset is cleared
            if (releasedTop !== null) {
                setReleasedTop(null);
                setVisualOffset(0);
            }
        },
        getGeometry: () => {
            const node = rootRef.current;
            if (!node)
                return null;
            const rect = node.getBoundingClientRect();
            const currentTop = style ? getNumericTop(style.top) : null;
            return {
                top: currentTop ?? rect.top,
                height: rect.height,
            };
        },
    };
    // Store handle in ref for external access
    handleRef.current = handle;
    // Forward the DOM ref
    useImperativeHandle(forwardedRef, () => rootRef.current, []);
    // If style.top has updated since we released, clear the visual offset immediately during render.
    const currentTop = style ? getNumericTop(style.top) : null;
    if (releasedTop !== null && currentTop !== null && currentTop !== releasedTop) {
        setReleasedTop(null);
        setVisualOffset(0);
    }
    // Safety timeout if parent didn't update style.top
    useEffect(() => {
        if (releasedTop !== null) {
            const timer = setTimeout(() => {
                setReleasedTop(null);
                setVisualOffset(0);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [releasedTop]);
    // Use useLayoutEffect to sync geometry after DOM mutations but before paint
    // This ensures the visual offset is cleared at the right time in the render cycle
    useLayoutEffect(() => {
        if (pendingSyncRef.current && releasedTop !== null) {
            pendingSyncRef.current = false;
            // Defer to next frame to ensure React commit phase is complete
            requestAnimationFrame(() => {
                setReleasedTop(null);
                setVisualOffset(0);
            });
        }
    }, [releasedTop]);
    const handlePointerDown = (event) => {
        event.preventDefault();
        rootRef.current?.focus();
        rootRef.current?.setPointerCapture(event.pointerId);
        dragRef.current = { pointerId: event.pointerId, startY: event.clientY };
        setIsDragging(true);
        setReleasedTop(null);
        setVisualOffset(0);
    };
    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId)
            return;
        const raw = event.clientY - drag.startY;
        const clamped = Math.max(minDeltaY ?? -Infinity, Math.min(maxDeltaY ?? Infinity, raw));
        setVisualOffset(clamped);
    };
    const stopDragging = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId)
            return;
        const node = rootRef.current;
        if (node?.hasPointerCapture(event.pointerId))
            node.releasePointerCapture(event.pointerId);
        const rawDelta = event.clientY - drag.startY;
        const totalDelta = Math.max(minDeltaY ?? -Infinity, Math.min(maxDeltaY ?? Infinity, rawDelta));
        dragRef.current = null;
        setIsDragging(false);
        if (totalDelta !== 0) {
            const currentTop = style ? getNumericTop(style.top) : null;
            if (currentTop !== null) {
                setReleasedTop(currentTop);
                pendingSyncRef.current = true;
            }
            else {
                setVisualOffset(0);
            }
            commitRef.current(splitterIndex, totalDelta, availableRef.current);
        }
        else {
            setVisualOffset(0);
        }
    };
    const handleKeyDown = (event) => {
        let delta = null;
        const step = event.shiftKey ? 16 : 8;
        switch (event.key) {
            case "ArrowUp":
                delta = -step;
                break;
            case "ArrowDown":
                delta = step;
                break;
            case "Home":
                delta = minDeltaY ?? null;
                break;
            case "End":
                delta = maxDeltaY ?? null;
                break;
            default:
                return;
        }
        if (delta === null || delta === 0)
            return;
        event.preventDefault();
        const clamped = Math.max(minDeltaY ?? -Infinity, Math.min(maxDeltaY ?? Infinity, delta));
        if (clamped !== 0) {
            commitRef.current(splitterIndex, clamped, availableRef.current);
        }
    };
    const cls = [
        "rsc-splitter",
        isDragging ? "rsc-splitter--dragging" : "",
        className ?? "",
    ].filter(Boolean).join(" ");
    const previewTop = typeof topHeight === "number" ? topHeight + visualOffset : undefined;
    const previewBottom = typeof bottomHeight === "number" ? bottomHeight - visualOffset : undefined;
    const topLbl = badgeTopLabel ?? "Top";
    const bottomLbl = badgeBottomLabel ?? "Bottom";
    const badgeText = isDragging && typeof previewTop === "number" && typeof previewBottom === "number" && typeof available === "number" && available > 0
        ? `${topLbl} ${Math.round(previewTop)}px (${Math.round((previewTop / available) * 100)}%) ↕ ${bottomLbl} ${Math.round(previewBottom)}px (${Math.round((previewBottom / available) * 100)}%)`
        : null;
    const ariaValueNow = typeof topHeight === "number" && typeof bottomHeight === "number" && topHeight + bottomHeight > 0
        ? Math.round((topHeight / (topHeight + bottomHeight)) * 100)
        : undefined;
    return (_jsx("div", { ref: rootRef, className: cls, role: "separator", "aria-orientation": "horizontal", tabIndex: 0, "aria-label": ariaLabel ?? title, "aria-valuenow": ariaValueNow, "aria-valuemin": 0, "aria-valuemax": 100, style: {
            ...style,
            transform: visualOffset !== 0 ? `translateY(${visualOffset}px)` : undefined,
        }, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: stopDragging, onPointerCancel: stopDragging, onDoubleClick: onDoubleClick, onKeyDown: handleKeyDown, title: title, children: badgeText ? _jsx("div", { className: "rsc-splitter__badge", children: badgeText }) : null }));
}));
ChartSplitterInner.displayName = "ChartSplitter";
export const ChartSplitter = memo(forwardRef(function ChartSplitter({ splitterIndex, available, onCommitDelta, onDoubleClick, className, style, title, minDeltaY, maxDeltaY, topHeight, bottomHeight, ariaLabel, badgeTopLabel, badgeBottomLabel, handleRef, }, forwardedRef) {
    const innerHandleRef = useRef(null);
    // Expose the handle to the parent via handleRef
    useImperativeHandle(handleRef, () => innerHandleRef.current, [innerHandleRef]);
    return (_jsx(ChartSplitterInner, { ref: forwardedRef, splitterIndex: splitterIndex, available: available, onCommitDelta: onCommitDelta, onDoubleClick: onDoubleClick, className: className, style: style, title: title, minDeltaY: minDeltaY, maxDeltaY: maxDeltaY, topHeight: topHeight, bottomHeight: bottomHeight, ariaLabel: ariaLabel, badgeTopLabel: badgeTopLabel, badgeBottomLabel: badgeBottomLabel }));
}));
ChartSplitter.displayName = "ChartSplitter";
